import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

import type {
    NormalizedBlockEntity,
    DenormalizedBlockEntity,
    NormalizedSectionEntity,
    DenormalizedSectionEntity,
    NormalizedColumnEntity,
    DenormalizedColumnEntity,
    // ColumnEntity,
    // ColumnStructureEntity,
    WebLegacyWidgetEntity,
} from '@web/types/structure/structure.type';

import { AppFacade } from '@web/store/app/app.facade';
import { SpecialBlocksService } from './special-blocks.service';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';

@Injectable({
    providedIn: 'root',
})
export class ProductRestrictionFacade {
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly specialBlocksService: SpecialBlocksService = inject(SpecialBlocksService);

    public blockRestricted$(block: NormalizedBlockEntity | DenormalizedBlockEntity): Observable<boolean> {
        const data$ = combineLatest([this.appFacade.isEcommerceMode$, this.ensureBlockStructure$(block)]);

        return data$.pipe(
            map(
                ([isEcommerceMode, blockEntity]) =>
                    isEcommerceMode && this.specialBlocksService.blockContainsProductWidget(blockEntity)
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public sectionRestricted$(section: NormalizedSectionEntity | DenormalizedSectionEntity): Observable<boolean> {
        return combineLatest([this.appFacade.isEcommerceMode$, this.ensureSectionStructure$(section)]).pipe(
            map(
                ([isEcommerceMode, structSection]) =>
                    isEcommerceMode &&
                    !!structSection &&
                    this.specialBlocksService.sectionContainsProductWidget(structSection)
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public columnRestricted$(column: NormalizedColumnEntity | DenormalizedColumnEntity): Observable<boolean> {
        return combineLatest([this.appFacade.isEcommerceMode$, this.ensureColumnStructure$(column)]).pipe(
            map(
                ([isEcommerceMode, structColumn]) =>
                    isEcommerceMode &&
                    !!structColumn &&
                    this.specialBlocksService.columnContainsProductWidget(structColumn)
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public widgetRestricted$(widget: WebLegacyWidgetEntity): Observable<boolean> {
        return this.appFacade.isEcommerceMode$.pipe(
            map((isEcommerceMode) => isEcommerceMode && this.specialBlocksService.isCrmProductWidget(widget)),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private ensureBlockStructure$(
        block: NormalizedBlockEntity | DenormalizedBlockEntity
    ): Observable<DenormalizedBlockEntity | null> {
        if (this.specialBlocksService.isBlockStructureEntity(block)) {
            return of(block);
        }
        return this.blocksFacade.blockStructureEntityById$(block.id);
    }

    private ensureSectionStructure$(
        section: NormalizedSectionEntity | DenormalizedSectionEntity
    ): Observable<DenormalizedSectionEntity | null> {
        if (this.specialBlocksService.isDenormalizedSection(section)) {
            return of(section);
        }
        return this.sectionsFacade.denormalizedSectionById$(section?.id);
    }

    private ensureColumnStructure$(
        column: NormalizedColumnEntity | DenormalizedColumnEntity
    ): Observable<DenormalizedColumnEntity | null> {
        if (this.specialBlocksService.isDenormalizedColumn(column)) {
            return of(column);
        }
        return this.columnsFacade.denormalizedColumnById$(column?.id);
    }
}
